/**
 * Hook for tracking scroll position and determining which threads should be sticky.
 * Uses hysteresis to prevent flickering at threshold boundaries.
 */

import { useState, useEffect, RefObject } from "react";
import type { LaneThread, OtherThreadSegment } from "../types";

interface UseStickyHeadersResult {
  /** Set of thread IDs that should be shown in sticky header */
  stickyLaneThreads: Set<string>;
  /** Index of the "other thread" segment currently sticky, or null */
  stickyOtherThreadSegment: number | null;
}

interface UseStickyHeadersParams {
  containerRef: RefObject<HTMLDivElement>;
  messageRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
  laneThreads: LaneThread[];
  threadMessageIndices: Map<string, { first: number; last: number }>;
  otherThreadSegments: OtherThreadSegment[];
  updateLanePositions: () => void;
}

/**
 * Tracks scroll position and determines which threads should be sticky.
 * Uses hysteresis buffer to prevent flickering when header height changes.
 */
export function useStickyHeaders({
  containerRef,
  messageRefs,
  laneThreads,
  threadMessageIndices,
  otherThreadSegments,
  updateLanePositions,
}: UseStickyHeadersParams): UseStickyHeadersResult {
  const [stickyLaneThreads, setStickyLaneThreads] = useState<Set<string>>(
    new Set()
  );
  const [stickyOtherThreadSegment, setStickyOtherThreadSegment] = useState<
    number | null
  >(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let rafId: number | null = null;
    let lastStickyThreadsStr = "";
    let lastStickyOtherSegment: number | null = null;

    const handleScroll = () => {
      // Throttle with requestAnimationFrame to prevent flickering
      if (rafId) return;

      rafId = requestAnimationFrame(() => {
        rafId = null;
        const containerRect = container.getBoundingClientRect();

        // Calculate sticky header height based on PREVIOUS sticky state
        // This prevents threshold instability when header height changes
        const prevStickyCount =
          lastStickyThreadsStr.split(",").filter(Boolean).length +
          (lastStickyOtherSegment !== null ? 1 : 0);
        const stickyHeaderHeight = prevStickyCount > 0 ? 16 + prevStickyCount * 20 : 0;

        // Hysteresis buffer to prevent flickering at threshold boundaries
        // Must be larger than the header height change when unsticking
        const hysteresisBuffer = Math.max(stickyHeaderHeight + 8, 24);

        // Track sticky lane threads
        const newStickyThreads = new Set<string>();

        laneThreads.forEach((lane) => {
          const indices = threadMessageIndices.get(lane.threadId);
          if (!indices) return;

          const firstMsgEl = messageRefs.current.get(`msg-${indices.first}`);
          const lastMsgEl = messageRefs.current.get(`msg-${indices.last}`);

          if (firstMsgEl && lastMsgEl) {
            const firstRect = firstMsgEl.getBoundingClientRect();
            const lastRect = lastMsgEl.getBoundingClientRect();

            const wasSticky = lastStickyThreadsStr.includes(lane.threadId);

            // Use hysteresis: different thresholds for becoming vs leaving sticky state
            const threshold = containerRect.top + stickyHeaderHeight;
            const firstPastTop = wasSticky
              ? firstRect.top < threshold + hysteresisBuffer // Stay sticky longer when scrolling up
              : firstRect.top < threshold; // Normal threshold to become sticky
            const lastStillVisible = wasSticky
              ? lastRect.bottom > threshold - hysteresisBuffer // Stay sticky longer when scrolling down
              : lastRect.bottom > threshold; // Normal threshold

            if (firstPastTop && lastStillVisible) {
              newStickyThreads.add(lane.threadId);
            }
          }
        });

        // Only update state if changed (to prevent unnecessary re-renders)
        const newStickyThreadsStr = Array.from(newStickyThreads)
          .sort()
          .join(",");
        if (newStickyThreadsStr !== lastStickyThreadsStr) {
          lastStickyThreadsStr = newStickyThreadsStr;
          setStickyLaneThreads(newStickyThreads);
        }

        // Track sticky "other thread" segment
        let newStickyOtherSegment: number | null = null;
        const threshold = containerRect.top + stickyHeaderHeight;

        for (let i = 0; i < otherThreadSegments.length; i++) {
          const segment = otherThreadSegments[i];
          const firstMsgEl = messageRefs.current.get(`msg-${segment.firstIndex}`);
          const lastMsgEl = messageRefs.current.get(`msg-${segment.lastIndex}`);

          if (firstMsgEl && lastMsgEl) {
            const firstRect = firstMsgEl.getBoundingClientRect();
            const lastRect = lastMsgEl.getBoundingClientRect();

            const wasSticky = lastStickyOtherSegment === i;
            const firstPastTop = wasSticky
              ? firstRect.top < threshold + hysteresisBuffer
              : firstRect.top < threshold;
            const lastStillVisible = wasSticky
              ? lastRect.bottom > threshold - hysteresisBuffer
              : lastRect.bottom > threshold;

            if (firstPastTop && lastStillVisible) {
              newStickyOtherSegment = i;
            }
          }
        }

        // Only update state if changed
        if (newStickyOtherSegment !== lastStickyOtherSegment) {
          lastStickyOtherSegment = newStickyOtherSegment;
          setStickyOtherThreadSegment(newStickyOtherSegment);
        }

        // Update lane positions after sticky state changes
        updateLanePositions();
      });
    };

    container.addEventListener("scroll", handleScroll);
    // Initial check
    handleScroll();

    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [
    containerRef,
    messageRefs,
    laneThreads,
    threadMessageIndices,
    otherThreadSegments,
    updateLanePositions,
  ]);

  return {
    stickyLaneThreads,
    stickyOtherThreadSegment,
  };
}
